import React from 'react'
import { Box, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

export interface FieldGroupHeadingProps {
  children: React.ReactNode
  /** Tighter top gap for a heading sitting directly under the card title. */
  underCardTitle?: boolean
  sx?: SxProps<Theme>
}

/**
 * A subsection heading that groups a cluster of fields *inside* a card,
 * subordinate to the teal `AdminFormSection` card title - e.g. "Operational
 * Flags" above toggles or "Business Address" above address fields.
 *
 * Uppercase neutral-grey label with a subtle 1px divider rule trailing to the
 * right. Deliberately NOT teal (teal is reserved for card titles) and uses no
 * chips, fills, backgrounds or nested cards. Render inside a full-width
 * `<Grid item xs={12}>` so it sits on its own row.
 */
const FieldGroupHeading: React.FC<FieldGroupHeadingProps> = ({
  children,
  underCardTitle = false,
  sx,
}) => (
  <Box
    sx={[
      {
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mt: underCardTitle ? '8px' : '21px',
        mb: '7px',
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <Typography
      component="span"
      sx={{
        textTransform: 'uppercase',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.04em',
        color: '#546E7A',
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}
    >
      {children}
    </Typography>
    <Box sx={{ flex: 1, borderBottom: '1px solid', borderColor: 'divider' }} />
  </Box>
)

export default FieldGroupHeading
